import _ from 'lodash'
import store from '@/store'
import { formatPriceWithoutCurrency } from '@/core/localization'
import utils from '@/core/utils'
import facebookPixel from '@/core/facebook-pixel'

/* eslint-disable */

/*
  - search
  + add to cart
  + remove from cart
  + purchase
  + checkout process
  + set checkout step
  + get the currency
  - add events for all
*/

// Helper Functions

function createEventObject(event, data, callback) {
  const eventObject = {
    event,
    ...data
  }

  if (store.getters.clientModel.impersonating || store.getters.clientModel.isSuperUser) {
    eventObject['dimension3'] = 'seller'
  } else {
    eventObject['dimension3'] = 'buyer'
  }

  if (store.getters.clientModel.user && !_.has(data, 'account_type')) {
    eventObject['account_type'] = getAccountType(store.getters.clientModel.user.isCompanyCustomer)
  }

  if (callback) {
    eventObject['eventCallback'] = callback
  }

  return eventObject
}

function addEvent (eventObject) {
  const dataLayer = _.get(window, 'dataLayer')
  
  dataLayer.push({ ecommerce: null })
  dataLayer.push(eventObject);

  facebookPixel.trackEvent(eventObject)
}

function createGtmItem(id, name, vehicleModel, category, price, quantity, listType, brand) {
  const gtmItem = {
    item_id: id,
    item_name: name,
    price,
    quantity
  }
  if (brand) gtmItem.item_brand = brand
  if (category) gtmItem.item_category = category
  if (vehicleModel) gtmItem.vehicle_model = vehicleModel
  if (listType) gtmItem.item_list_id = listType

  return gtmItem
}

function createGtmItemsFromCart(cart) {
  let value = 0;
  const items = _.map(cart.rows, (row) => {
    const price = parseFloat(formatPriceWithoutCurrency(row.price))
    value += price * row.quantity

    return {
      item_id: row.itemId,
      item_name: row.displayName,
      item_brand: row.item.productBrand,
      item_category: row.item.purchasedOnCategory,
      vehicle_model: row.item.purchasedToVehicle,
      price,
      quantity: row.quantity,
    }
  })

  return { value, items }
}

async function hashText(text) {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

function addTagEventGeneric(event, category, action, label, value) {
  addEvent({
    'event': event,
    'eventCategory': category,
    'action': action,
    'label': label,
    'value': value
  })
}

// Events

function addToCart(items) {
  const itemList = _.isArray(items) ? items : [items]
  const data = {
    currency: store.getters.clientModel.currency,
    value: itemList.reduce((tot, val) => tot + parseFloat(val.price) * val.quantity, 0),
    items: itemList,
  }
  const eventObject = createEventObject('add_to_cart', { ecommerce: data })
  addEvent(eventObject)

  const chatSessionId = utils.getQueryStringParam('chatSession')
  if(chatSessionId){
    const storedSessionId = localStorage.getItem('sessionId')
    addEvent({
      event: 'chat_lead_to_cart',
      chatbot_session_id: storedSessionId,
      chatbot_product_id: data.items && data.items.length > 0 ? data.items[0].item_id : null,
      timestamp: new Date().toISOString()
    })
    localStorage.setItem('chat_conversion', JSON.stringify({ chatSessionId }))
  }
}

function removeFromCart(items) {
  const itemList = _.isArray(items) ? items : [items]
  const data = {
    currency: store.getters.clientModel.currency,
    value: itemList.reduce((tot, val) => tot + parseFloat(val.price) * val.quantity, 0),
    items: itemList,
  }
  const eventObject = createEventObject('remove_from_cart', { ecommerce: data })

  addEvent(eventObject)
}

function addCheckoutStep(event, cart, info = {}) {
  const availableEvents = ['begin_checkout', 'add_shipping_info', 'add_payment_info', 'view_cart']
  if (!availableEvents.includes(event)) return

  const { value, items } = createGtmItemsFromCart(cart)

  const data = {
    currency: store.getters.clientModel.currency,
    value,
    items,
    ...info,
  }

  const eventObject = createEventObject(event, { ecommerce: data })
  addEvent(eventObject)
}

function addPurchase(order) {
  let value = 0
  const items = _.map(order.orderRows, (row) => {

    const price = parseFloat(formatPriceWithoutCurrency(row.priceForEachItem))
    value += price * row.quantity

    return {
      item_id: row.artNr,
      item_name: row.artName,
      item_brand: row.brandName,
      affiliation: 'OIE - Web',
      item_category: row.categoryUrl,
      vehicle_model: row.purchasedToVehicle,
      price: price,
      quantity: row.quantity,
    }
  })

  const data = {
    currency: store.getters.clientModel.currency,
    transaction_id: order.orderHead.orderNr,
    value,
    shipping: parseFloat(formatPriceWithoutCurrency(order.orderHead.shipmentCost)),
    tax: parseFloat(formatPriceWithoutCurrency(order.orderHead.vatSum)),
    user_email: order.orderHead.userName,
    items
  }

  const eventObject = createEventObject('purchase', { ecommerce: data })

  addEvent(eventObject)
}

function addSignUpEvent(isCompanyCustomer) {
  const eventObject = createEventObject('sign_up', { account_type: getAccountType(isCompanyCustomer) })
  addEvent(eventObject)
}

function addSearchEvent(typeOfSearch, section, query, hits) {
  addTagEventGeneric('Search', section, typeOfSearch, query, hits)
}

function addViewItem(data) {
  const items = (data && data.items) || []

  // GA4 recommends currency and value on view_item. Value is only meaningful once a price is
  // known, so an item without one is left without it rather than reported as worth zero.
  const value = items.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0)

  const ecommerce = { ...data, currency: store.getters.clientModel.currency }
  if (value > 0) ecommerce.value = value

  const eventObject = createEventObject('view_item', { ecommerce })
  addEvent(eventObject)
}

function getAccountType(isCompanyCustomer) {
  return isCompanyCustomer ? 'company' : 'individual'
}

function chatSessionStart(sessionId) {
  addEvent({
    event: 'chat_session_start',
    chatbot_session_id: sessionId,
    timestamp: new Date().toISOString()
  })
}

async function chatPrompt(sessionId, promptText) {
  const hashedPrompt = await hashText(promptText);
  addEvent({
    event: 'chat_prompt',
    chatbot_session_id: sessionId,
    prompt_text: hashedPrompt,
    timestamp: new Date().toISOString()
  })
}

function chatResponseOutcome(sessionId, response_type) {
  addEvent({
    event: 'chat_response_outcome',
    chatbot_session_id: sessionId,
    chatbot_response_type: response_type
  })
}

function chatNoConversion(sessionId) {
  addEvent({
    event: 'chat_no_conversion',
    chatbot_session_id: sessionId,
    timestamp: new Date().toISOString()
  })
}

function chatSessionEnd(sessionId, messageCount, endReason) {
  endReason = endReason || 'unknown';
  addEvent({
    event: 'chat_session_end',
    chatbot_session_id: sessionId,
    chatbot_total_messages: messageCount,
    chatbot_end_reason: endReason,
    timestamp: new Date().toISOString()
  })
}

function chatProductClick(sessionId, productNumber) {
  addEvent({
    event: 'chat_product_click',
    chatbot_session_id: sessionId,
    chatbot_product_id: productNumber,
    timestamp: new Date().toISOString()
  })
}

function chatWebContentClick(url, sessionId) {
  addEvent({
    event: 'chat_webcontent_click',
    chatbot_session_id: sessionId,
    chatbot_webcontent_url: url,
    timestamp: new Date().toISOString()
  })
}

export default {
  addSearchEvent,
  addToCart,
  removeFromCart,
  addPurchase,
  createGtmItem,
  addCheckoutStep,
  addSignUpEvent,
  addViewItem,
  chatSessionStart,
  chatPrompt,
  chatResponseOutcome,
  chatNoConversion,
  chatSessionEnd,
  chatProductClick,
  chatWebContentClick
}
